import { useState, useRef, useEffect, useCallback } from "react";
import {
  ExternalLink,
  Copy,
  Check,
  Box,
  Link2,
  Ghost,
  ChevronDown,
} from "lucide-react";
import AttackBoxLaunchContent from "./attackBoxLaunchContent";

export default function AttackBoxLaunchModal({ url, onClose, onOpen, isMultiVm = false }) {
  const [copied, setCopied] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const bodyRef = useRef(null);

  // Show the scroll hint only while there is more content below the fold.
  const updateScrollHint = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollHint(remaining > 24);
  }, []);

  useEffect(() => {
    updateScrollHint();
    const raf = requestAnimationFrame(updateScrollHint);
    return () => cancelAnimationFrame(raf);
  }, [updateScrollHint]);

  const handleScrollDown = () => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollBy({ top: el.clientHeight * 0.8, behavior: "smooth" });
  };

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center p-4 bg-(--color-bg)/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-sm border border-(--color-primary)/30 bg-(--color-surface,var(--color-bg)) shadow-[0_0_40px_rgba(0,255,0,0.12)] qv-noise"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glow accent at the top edge */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-(--color-primary)/70 to-transparent" />
        <div className="pointer-events-none absolute -top-24 left-1/2 h-48 w-96 -translate-x-1/2 rounded-full bg-(--color-primary)/10 blur-3xl" />

        {/* Header */}
        <div className="relative flex shrink-0 items-center justify-between border-b border-(--color-border) px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-(--color-primary)/10 border border-(--color-primary)/40 text-(--color-primary) shadow-[0_0_15px_rgba(0,255,0,0.2)]">
              <Box className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-(--color-text-primary) leading-tight">
                Before You Open the AttackBox
              </h2>
              <p className="text-[11px] text-(--color-text-muted)">
                Your browser-based lab machine
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-(--color-text-muted) hover:bg-(--color-bg)/60 hover:text-(--color-text-primary) transition-colors text-lg leading-none"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Scrollable body */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            ref={bodyRef}
            onScroll={updateScrollHint}
            className="min-h-0 flex-1 overflow-y-auto px-6 py-5"
          >
            <AttackBoxLaunchContent isMultiVm={isMultiVm} />
          </div>

          {/* Fade mask so content tucks under the hint */}
          <div
            className={`pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-linear-to-t from-(--color-surface,var(--color-bg)) to-transparent transition-opacity ${
              showScrollHint ? "opacity-100" : "opacity-0"
            }`}
          />

          {/* Scroll-down hint */}
          <button
            type="button"
            onClick={handleScrollDown}
            aria-label="Scroll down for more"
            className={`absolute bottom-3 left-1/2 -translate-x-1/2 flex h-9 w-9 items-center justify-center rounded-full border border-(--color-primary)/50 bg-(--color-surface,var(--color-bg)) text-(--color-primary) shadow-[0_0_18px_rgba(0,255,0,0.35)] transition-all duration-200 animate-bounce hover:bg-(--color-primary)/15 ${
              showScrollHint
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none"
            }`}
          >
            <ChevronDown className="h-5 w-5" />
          </button>
        </div>

        {/* Footer — link + actions */}
        <div className="relative shrink-0 border-t border-(--color-border) bg-(--color-bg)/40 px-6 py-4 space-y-3">
          <div className="flex items-stretch gap-2">
            <div className="flex flex-1 min-w-0 items-center gap-2 rounded-lg border border-(--color-border) bg-(--color-bg)/60 px-3 py-2">
              <Link2 className="h-4 w-4 text-(--color-primary) shrink-0" />
              <span className="flex-1 min-w-0 truncate font-mono text-[11px] text-(--color-text-primary)">
                {url || "—"}
              </span>
            </div>
            <button
              type="button"
              onClick={handleCopy}
              className={`shrink-0 px-3 py-2 rounded-lg border font-semibold text-sm flex items-center justify-center gap-1.5 transition-all ${
                copied
                  ? "border-(--color-primary)/50 bg-(--color-primary)/15 text-(--color-primary)"
                  : "border-(--color-border) bg-(--color-bg)/60 text-(--color-text-primary) hover:bg-(--color-bg)/80"
              }`}
            >
              {copied ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-(--color-primary)/40 bg-(--color-primary)/8 px-3.5 py-3 shadow-[0_0_18px_rgba(0,255,0,0.12)]">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-(--color-primary)/15 border border-(--color-primary)/40 text-(--color-primary)">
              <Ghost className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-(--color-primary) uppercase tracking-wide mb-0.5">
                Open in incognito
              </p>
              <p className="text-[11px] leading-relaxed text-(--color-text-primary)/90">
                Strongly recommended to avoid cached sessions. Browsers can't
                open incognito automatically — copy the link and paste it into a
                private window (
                <kbd className="bg-(--color-bg)/70 border border-(--color-primary)/40 rounded px-1.5 py-0.5 font-mono text-[10px] font-bold text-(--color-primary)">
                  Ctrl + Shift + N
                </kbd>
                ).
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => onOpen?.()}
            className="group w-full px-4 py-2.5 rounded-lg bg-(--color-primary)/15 border border-(--color-primary)/50 text-(--color-primary) font-bold text-sm flex items-center justify-center gap-2 transition-all hover:bg-(--color-primary)/25 hover:shadow-[0_0_20px_rgba(0,255,0,0.35)]"
          >
            <ExternalLink className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            Open in Browser
          </button>
        </div>
      </div>
    </div>
  );
}
